import { useEffect, useState } from "react";
import type { CSSProperties } from "react";

/** Design-time max heights — FLIP morph needs distinct targets per view. */
const TRAY_HEIGHT_CONFIG = {
  PRODUCT_SELECTION: { max: 650, ratio: 0.92 },
  PRODUCT_DETAILS: { max: 500, ratio: 0.78 },
  VARIANT_SELECTION: { max: 520, ratio: 0.8 },
} as const;

const MIN_TRAY_HEIGHT = 320;

/** Vertical space the floating close button occupies above a sheet: size-9 (36px) + mb-3 (12px).
    Every sheet height is capped at `container - this` so the button is always on-screen. */
export const SHEET_CLOSE_RESERVE_PX = 48;

export type FamilyTrayView = keyof typeof TRAY_HEIGHT_CONFIG;

export type FamilyTrayHeights = Record<FamilyTrayView, number>;

const getDefaultHeights = (): FamilyTrayHeights =>
  Object.fromEntries(
    Object.entries(TRAY_HEIGHT_CONFIG).map(([key, value]) => [key, value.max]),
  ) as FamilyTrayHeights;

const computeHeights = (containerHeight: number): FamilyTrayHeights => {
  // Hard ceiling — anything taller pushes the floating close button off the top of the container
  const available = Math.max(0, containerHeight - SHEET_CLOSE_RESERVE_PX);
  // The 320px floor must itself yield to `available` on very short containers, else it wins the
  // Math.max below and re-clips the button
  const floor = Math.min(MIN_TRAY_HEIGHT, available);

  return (
    Object.entries(TRAY_HEIGHT_CONFIG) as [FamilyTrayView, typeof TRAY_HEIGHT_CONFIG[FamilyTrayView]][]
  ).reduce((acc, [view, { max, ratio }]) => {
    acc[view] = Math.max(floor, Math.min(max, available, Math.round(containerHeight * ratio)));
    return acc;
  }, {} as FamilyTrayHeights);
};

/** Scales tray step heights to the checkout container while preserving distinct morph targets. */
export const useFamilyTrayHeights = (container: HTMLElement | null): FamilyTrayHeights => {
  const [heights, setHeights] = useState(getDefaultHeights);

  useEffect(() => {
    if (!container) return;

    const update = () => {
      const next = computeHeights(container.clientHeight);
      setHeights((prev) =>
        prev.PRODUCT_SELECTION === next.PRODUCT_SELECTION &&
        prev.PRODUCT_DETAILS === next.PRODUCT_DETAILS &&
        prev.VARIANT_SELECTION === next.VARIANT_SELECTION
          ? prev
          : next,
      );
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(container);
    return () => observer.disconnect();
  }, [container]);

  return heights;
};

export const familyTrayHeightStyle = (heights: FamilyTrayHeights): CSSProperties =>
  ({
    "--tray-h-product-selection": `${heights.PRODUCT_SELECTION}px`,
    "--tray-h-product-details": `${heights.PRODUCT_DETAILS}px`,
    "--tray-h-variant-selection": `${heights.VARIANT_SELECTION}px`,
  } as CSSProperties);
